// Runtime configuration resolution (docs/04 — the player must be deployable to
// play.<domain> and point at api.<domain> without a rebuild).
//
// Resolution order for each value: URL query param → localStorage (persisted
// from a previous param) → built-in default. Passing `?api=` once pins it for
// the device; a factory reset clears localStorage back to defaults.

const LS_API = 'sl_api_base';
const LS_CMS = 'sl_cms_url';

function resolve(param: string, lsKey: string, fallback: string): string {
  const q = new URLSearchParams(location.search);
  const fromQuery = q.get(param);
  if (fromQuery !== null) {
    try {
      localStorage.setItem(lsKey, fromQuery);
    } catch {
      /* private mode / storage disabled — fall through to the query value */
    }
    return fromQuery;
  }
  try {
    const stored = localStorage.getItem(lsKey);
    if (stored !== null) return stored;
  } catch {
    /* ignore */
  }
  return fallback;
}

/** Strip a single trailing slash so we can join paths uniformly. */
function trimTrailingSlash(s: string): string {
  return s.endsWith('/') && s.length > 1 ? s.slice(0, -1) : s;
}

export interface Config {
  /** API origin/base. Default '' → same-origin relative (works behind a proxy). */
  apiBase: string;
  /** CMS origin used to build the QR pair link. */
  cmsUrl: string;
  /** ?mock=1 → bundled fixtures + fake pairing, no backend needed (docs/04 dev mode). */
  mock: boolean;
}

export function loadConfig(): Config {
  const q = new URLSearchParams(location.search);
  return {
    // Resolution: ?api= query → localStorage → VITE_API_BASE baked at build → '' (same
    // origin). '' works behind a proxy (play.<domain> forwarding /v1/* to api.<domain>).
    // The deployed dev player has no proxy, so its build bakes VITE_API_BASE = the API
    // Worker origin (absolute, no /v1 suffix — apiUrl() adds the /v1 path). ?api= still
    // overrides per-device for field re-pointing.
    apiBase: trimTrailingSlash(
      resolve('api', LS_API, (import.meta.env.VITE_API_BASE as string | undefined) ?? ''),
    ),
    cmsUrl: trimTrailingSlash(resolve('cms', LS_CMS, 'https://app.seencast.app')),
    mock: q.get('mock') === '1',
  };
}

/** Join the API base with a path that already starts with '/'. */
export function apiUrl(cfg: Config, path: string): string {
  return `${cfg.apiBase}${path}`;
}

/**
 * The live-channel WebSocket URL (docs/04 §Live channel). Derives ws:// from
 * http:// and wss:// from https:// off the API origin — `apiBase` when set,
 * otherwise the page origin (same-origin, works behind the play.<domain> proxy).
 * The device token is NEVER placed in the URL — it rides the WS subprotocol
 * (`seencast-device.<token>`), so it can't leak into logs/referrers.
 */
export function liveWsUrl(cfg: Config, origin: string = location.origin): string {
  const base = cfg.apiBase || origin;
  const wsOrigin = base.replace(/^http/i, 'ws'); // http→ws, https→wss
  return `${wsOrigin}/v1/player/ws`;
}
